import { ReportOption } from '@/modules/issuer-dashboard/components/ReportsSelectPaginate.tsx';
import { SelectPaginateOption } from 'ui';
import {
  BulkCommunicationCertificateConfig,
  BulkCommunicationReportConfig,
  BulkReportType,
  CreateCommunicationRequest,
  ReportFormat,
  ShareCertificateResponse,
} from 'api';

export type CommunicationPayload = Omit<
  CreateCommunicationRequest,
  'bulk_report_config' | 'bulk_certificate_config'
> & {
  bulk_report_config?: BulkCommunicationReportConfig | null;
  bulk_certificate_config?: BulkCommunicationCertificateConfig | null;
};

export interface CommunicationFormValues
  extends Omit<
    CreateCommunicationRequest,
    'bulk_generate_attachments' | 'bulk_report_config' | 'bulk_certificate_config'
  > {
  bulk_generate_attachments: boolean;
  generate_report: boolean;
  generate_certificate: boolean;
  bulk_report_config: {
    report_type: BulkReportType;
    register_class_ids?: number[];
    start_date: string | null;
    end_date: string | null;
    financial_year?: number | null;
    dividend_event_id?: number;
    file_type: ReportFormat;
  };
  bulk_certificate_config: {
    register_class_ids: number[];
    file_type?: ReportFormat;
  };
}

export const buildCommunicationPayload = (
  values: CommunicationFormValues,
  selectedReports: ReportOption[],
  selectedCertificates: SelectPaginateOption<ShareCertificateResponse>[],
  text: string,
): CommunicationPayload => {
  const basePayload = {
    subject: values.subject,
    text,
    shareholder_ids: values.shareholder_ids,
  };

  if (!values.bulk_generate_attachments) {
    return {
      ...basePayload,
      report_ids: selectedReports?.map(({ value }) => value.id),
      certificate_ids: selectedCertificates?.map(({ value }) => value.id),
      bulk_generate_attachments: false,
      bulk_report_config: undefined,
      bulk_certificate_config: undefined,
    };
  }

  const bulkReportConfig: BulkCommunicationReportConfig | undefined = values.generate_report
    ? {
        ...values.bulk_report_config,
        register_class_ids: values.bulk_report_config.register_class_ids?.length
          ? values.bulk_report_config.register_class_ids
          : undefined,
        start_date: values.bulk_report_config.start_date || undefined,
        end_date: values.bulk_report_config.end_date || undefined,
        financial_year: values.bulk_report_config.financial_year || undefined,
        dividend_event_id: values.bulk_report_config.dividend_event_id || undefined,
      }
    : undefined;
  const bulkCertificateConfig: BulkCommunicationCertificateConfig | undefined =
    values.generate_certificate
      ? {
          ...values.bulk_certificate_config,
          register_class_ids: values.bulk_certificate_config.register_class_ids,
          file_type: values.bulk_certificate_config.file_type,
        }
      : undefined;

  return {
    ...basePayload,
    bulk_generate_attachments: true,
    bulk_report_config: bulkReportConfig ?? null,
    bulk_certificate_config: bulkCertificateConfig ?? null,
  };
};
